import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

// Signature hand-built neoclassical capitol silhouette + flowing legislative data lines.
// Architecture stays stable; the data-flow layer gets a very subtle parallax drift.
export default function CapitolBackdrop() {
  const reduce = useReducedMotion();
  const flowRef = useRef(null);

  useEffect(() => {
    if (reduce) return;
    let raf = 0;
    const onScroll = () => {
      raf = requestAnimationFrame(() => {
        if (flowRef.current) {
          const y = window.scrollY * 0.06;
          flowRef.current.style.transform = `translateY(${y}px)`;
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [reduce]);

  const cols = Array.from({ length: 8 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Flowing data lines layer */}
      <svg ref={flowRef} className="absolute inset-0 h-full w-full opacity-[0.5]" viewBox="0 0 1440 760" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="flowLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3F7FB5" stopOpacity="0" />
            <stop offset="45%" stopColor="#3F7FB5" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#087F83" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flowGold" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#D6A01D" stopOpacity="0" />
            <stop offset="50%" stopColor="#D6A01D" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#D6A01D" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[110, 210, 330, 470, 610].map((y, i) => (
          <g key={i}>
            <path
              d={`M-50 ${y} C 300 ${y - 40}, 620 ${y + 60}, 900 ${y - 20} S 1500 ${y + 30}, 1500 ${y}`}
              fill="none" stroke={i === 2 ? "url(#flowGold)" : "url(#flowLine)"} strokeWidth={i === 2 ? 1.6 : 1}
              className={reduce ? "" : "gf-dash"} style={{ animationDelay: `${i * 0.9}s` }}
            />
            <circle r="3" fill={i === 2 ? "#D6A01D" : "#3F7FB5"} className={reduce ? "" : "gf-node"} style={{ offsetPath: `path('M-50 ${y} C 300 ${y - 40}, 620 ${y + 60}, 900 ${y - 20} S 1500 ${y + 30}, 1500 ${y}')`, animationDelay: `${i * 1.4}s` }} />
          </g>
        ))}
      </svg>

      {/* Neoclassical capitol silhouette (stable) */}
      <svg className="absolute bottom-0 left-1/2 h-[74%] w-[1200px] max-w-none -translate-x-1/2 opacity-[0.16]" viewBox="0 0 1200 620" preserveAspectRatio="xMidYMax meet" fill="#9FC0DE">
        {/* dome */}
        <path d="M600 40 C 560 40 528 74 528 120 L672 120 C 672 74 640 40 600 40 Z" />
        <rect x="595" y="8" width="10" height="34" />
        <circle cx="600" cy="8" r="7" />
        {/* drum under dome */}
        <rect x="540" y="120" width="120" height="26" />
        <rect x="524" y="146" width="152" height="16" />
        {/* pediment */}
        <path d="M360 250 L600 176 L840 250 Z" />
        <rect x="352" y="250" width="496" height="20" />
        {/* colonnade */}
        {cols.map((_, i) => (
          <rect key={i} x={392 + i * 56} y="278" width="26" height="180" rx="3" />
        ))}
        <rect x="360" y="462" width="480" height="20" />
        {/* wings */}
        <rect x="150" y="330" width="200" height="152" />
        <rect x="850" y="330" width="200" height="152" />
        {[0, 1, 2, 3].map((i) => <rect key={`l${i}`} x={172 + i * 46} y="356" width="26" height="100" rx="3" />)}
        {[0, 1, 2, 3].map((i) => <rect key={`r${i}`} x={872 + i * 46} y="356" width="26" height="100" rx="3" />)}
        {/* base */}
        <rect x="120" y="482" width="960" height="40" />
        <rect x="80" y="522" width="1040" height="98" />
      </svg>
    </div>
  );
}
